/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import axios from 'axios';
import { signInUserConfig } from '../../config/signInUser.config';
import { apiConfig } from '@/config/apiConfig';
import toast from 'react-hot-toast';

const { toHeaderCase } = require('js-convert-case');

export interface ISignInUserMethods extends IPluginMethodMap {
  validateEmail(args: any): Promise<any>;
  signInUser(args: any): Promise<any>;
  getAccessToken(args: {
    apiURL: string;
    jwtToken: string;
    role: string;
  }): Promise<any>;

  getSignInType(args: { email: string; apiURL: string }): Promise<any>;
  sendOTP(args: { email: string; apiURL: string }): Promise<any>;
  verifyOTP(args: { email: string; otp: string; apiURL: string }): Promise<any>;
  setPassword(args: {
    email: string;
    password: string;
    apiURL: string;
  }): Promise<any>;
}

export interface HeaderProps {
  headerName: string;
  headerValue: string;
}

export interface SignInProps {
  email: string;
  password?: string;
  otp?: string;
  jwtToken?: string;
  role?: string;
  apiURL: string;
  headers: HeaderProps;
  storeId?: string;
}

export class SignInUserService implements IAgentPlugin {
  readonly methods: ISignInUserMethods;
  constructor() {
    this.methods = {
      validateEmail: this.validateEmail.bind(this),
      signInUser: this.signInUser.bind(this),
      getSignInType: this.getSignInType.bind(this),
      sendOTP: this.sendOTP.bind(this),
      getAccessToken: this.getAccessToken.bind(this),
      verifyOTP: this.verifyOTP.bind(this),
      setPassword: this.setPassword.bind(this),
    };
  }

  async validateEmail(args: { email: string; apiURL: string }) {
    console.log(`args in sendOTP`, args);
    try {
      const res = await axios.get(
        `${apiConfig?.rest?.url}${signInUserConfig?.validateEmail}`,
        {
          params: {
            email: args?.email,
          },
        }
      );

      if (res?.data) {
        console.log(`validateEmail`, res?.data);
        return res?.data;
      }
    } catch (err: any) {
      console.log(`validateEmail error`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
  }

  async verifyOTP(args: { email: string; otp: string; apiURL: string }) {
    console.log(`args in sendOTP`, args);
    try {
      const res = await axios.post(
        `${apiConfig?.rest?.url}${signInUserConfig?.sendVerifyOTP}`,
        {
          email: args?.email,
          otp: args?.otp,
        }
      );

      if (res?.data) {
        console.log(`verifyOTP`, res?.data);
        return res?.data;
      }
    } catch (err: any) {
      console.log(`verifyOTP error`, err?.response?.data);
      toast.error(err?.response?.data?.error?.display_message);
      throw Error(err?.response?.data?.error?.display_message);
    }
  }

  async setPassword(args: { email: string; password: string; apiURL: string }) {
    console.log(`args in sendOTP`, args);
    try {
      const res = await axios.post(
        `${apiConfig?.rest?.url}${signInUserConfig?.setPassword}`,
        {
          email: args?.email,
          password: args?.password,
        }
      );

      if (res?.data) {
        console.log(`verifyOTP`, res?.data);
        return res?.data;
      }
    } catch (err: any) {
      console.log(`verifyOTP error`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
  }

  async sendOTP(args: { email: string; apiURL: string }) {
    console.log(`args in sendOTP`, args);
    try {
      const res = await axios.post(
        `${apiConfig?.rest?.url}${signInUserConfig?.sendOTPEP}`,
        {
          email: args?.email,
        }
      );
      if (res?.data) {
        console.log(`sendOTP`, res?.data);
        return res?.data;
      }
    } catch (err: any) {
      console.log(`sendOTP error`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
  }

  async getSignInType(args: { email: string; apiURL: string }) {
    try {
      const res = await axios.post(
        `${args?.apiURL}${signInUserConfig?.signInTypeEP}`,
        {
          email: args?.email,
        }
      );
      if (res?.data) {
        console.log(`getSignInType`, res?.data);
        if (res?.data?.signInType === 'otp') {
          await this.sendOTP(args);
        }
        return res?.data;
      }
    } catch (err: any) {
      console.log(`getSignInType error`, err?.response?.data);
      throw err?.response?.data?.error?.display_message;
    }
  }

  async getAccessToken(args: {
    apiURL: string;
    jwtToken: string;
    role: string;
    org: string;
  }) {
    try {
      const res = await axios.post(
        `${apiConfig?.rest?.url}${signInUserConfig?.accessTokenEP}`,
        {
          jwtToken: args?.jwtToken,
          role: args?.role,
          org: args?.org,
        }
      );
      if (res?.data) {
        console.log('getAccessToken =', res?.data);
        return res?.data;
      }
    } catch (err: any) {
      throw Error(
        err?.response?.data?.error?.display_message ??
          err?.response?.data?.error?.error_message ??
          err?.response?.data?.error_message
      );
    }
  }

  async signInUser(args: SignInProps) {
    console.log(`args in sign in user service`, args);
    let headers: any = {};
    headers[args?.headers?.headerName] = args?.headers?.headerValue;
    console.log(
      'login API end point:',
      `${apiConfig?.rest?.url}${signInUserConfig?.signInEP}`
    );
    // return true;
    try {
      const res = await axios.post(
        `${apiConfig?.rest?.url}${signInUserConfig?.signInEP}`,
        {
          email: args?.email,
          password: args?.password,
          otp: args?.otp,
          jwtToken: args?.jwtToken,
          role: args?.role,
        }
      );
      if (res?.data) {
        if (res?.data?.userRoles) {
          res.data.userRoles = this.formatRoles(res?.data?.userRoles);
          res.data.allUserRolesList = this.formatRole(res?.data?.userRoles);
        }
        if (res?.data?.orgRoles) {
          res.data.orgRolesfor = this.formatOrg(res?.data?.orgRoles);
        }
        // res.data.passwordRecentlyUpdated = false;
        return res?.data;
      }
    } catch (err: any) {
      console.log(`Sign In user error`, err);
      if (
        err?.response?.data?.error?.display_message?.startsWith(
          'user not found.'
        )
      ) {
        throw 'User Not Found!';
      }
      throw err?.response?.data?.error?.display_message;
    }
  }

  private formatRoles(roles: any[]) {
    let _res: any = [];
    for (const role of roles) {
      _res.push({
        ...(role || {}),
        title: role?.name ? toHeaderCase(role?.name) : role?.name,
      });
    }
    return _res;
  }

  private formatRole(role: any[]) {
    if (Array.isArray(role)) {
      return role?.map((item) => {
        let name = item?.name?.[0]?.toUpperCase();
        for (let i = 1; i < item?.name?.length; i++) {
          if (item?.name?.[i] == '_' || i === 0) {
            name += ' ' + item?.name?.[i + 1]?.toUpperCase();
            i++;
          } else name += item?.name?.[i];
        }
        return {
          ...item,
          label: name === 'Owner' ? 'Admin' : name,
        };
      });
    }
    return [];
  }
  private formatOrg(orgRoles: any[]) {
    return orgRoles.map(({ role, org }) => {
      const roleLabel = this.formatRoleName(role?.name);
      console.log(role, org);
      return {
        uuid: role?.uuid,
        org: org?.orgId ?? null,
        value: role?.name,
        label: this.formatRoleName(role?.name),
        orgId: org?.orgId ?? null,
        orgName: org?.name ?? null,
      };
    });
  }

  private formatRoleName(name: string): string {
    if (!name) return '';

    let formatted = name[0].toUpperCase();
    for (let i = 1; i < name.length; i++) {
      if (name[i] === '_') {
        formatted += ' ' + name[i + 1]?.toUpperCase();
        i++;
      } else {
        formatted += name[i].toLowerCase();
      }
    }

    return formatted === 'Owner'
      ? 'Admin'
      : formatted === 'Agent'
      ? 'HR Agent'
      : formatted;
  }
}
